// src/hooks/queries/useActividades.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { actividadesService } from '@/services/actividades';
import { toast } from '@/components/ui';
import type { ActividadFiltros, ActividadCreate, ActividadUpdate, ActividadListItem, ReordenarItem, ReparentRequest, BulkActualizarRequest, BulkCompletarRequest, BulkEliminarRequest } from '@/types';

export function useTiposActividad() {
  return useQuery({
    queryKey: ['actividades', 'catalogos', 'tipos'],
    queryFn: () => actividadesService.getTipos(),
    staleTime: 5 * 60 * 1000, // Cache 5 min
  });
}

export function useModulos() {
  return useQuery({
    queryKey: ['actividades', 'catalogos', 'modulos'],
    queryFn: () => actividadesService.getModulos(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useActividades(params?: ActividadFiltros) {
  return useQuery({
    queryKey: ['actividades', params],
    queryFn: () => actividadesService.list(params),
  });
}

export function useActividadesContadores() {
  return useQuery({
    queryKey: ['actividades', 'contadores'],
    queryFn: () => actividadesService.contadores(),
    refetchInterval: 60000,
  });
}

export function useActividad(id: string) {
  return useQuery({
    queryKey: ['actividades', id],
    queryFn: () => actividadesService.get(id),
    enabled: !!id,
  });
}

export function useMisActividadesHoy() {
  return useQuery({
    queryKey: ['actividades', 'hoy'],
    queryFn: () => actividadesService.misActividadesHoy(),
    refetchInterval: 60000, // Refrescar cada minuto
  });
}

export function useProximasActividades(dias: number = 7) {
  return useQuery({
    queryKey: ['actividades', 'proximas', dias],
    queryFn: () => actividadesService.proximasActividades(dias),
  });
}

export function useCrearActividad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ActividadCreate) => actividadesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Actividad creada correctamente');
    },
    onError: () => {
      toast.error('No se pudo crear la actividad');
    },
  });
}

export function useQuickCreateActividad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ActividadCreate) => actividadesService.create(data),
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey: ['actividades'] });

      const previousQueries = queryClient.getQueriesData<ActividadListItem[]>({
        queryKey: ['actividades'],
      });

      const optimisticItem: ActividadListItem = {
        id: `temp-${Date.now()}`,
        titulo: newData.titulo,
        area: newData.area ?? 'PERSONAL',
        estado: 'PENDIENTE',
        prioridad: newData.prioridad ?? 'A',
        tiempo_registrado_min: 0,
        es_recurrente: false,
        es_plantilla: false,
        es_backlog: false,
        en_foco: false,
        origen: 'MANUAL',
      };

      queryClient.setQueriesData<ActividadListItem[]>(
        { queryKey: ['actividades'] },
        (old) => {
          if (!Array.isArray(old)) return old;
          return [optimisticItem, ...old];
        }
      );

      return { previousQueries };
    },
    onError: (_err, _data, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error('No se pudo crear la actividad');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
    },
  });
}

export function useActualizarActividad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ActividadUpdate; silent?: boolean }) =>
      actividadesService.update(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['actividades'] });
      // Optimistically update all actividades caches with the new field values
      const previousQueries: [readonly unknown[], ActividadListItem[] | undefined][] = [];
      queryClient.getQueriesData<ActividadListItem[]>({ queryKey: ['actividades'] })
        .forEach(([queryKey, cached]) => {
          if (!Array.isArray(cached)) return;
          previousQueries.push([queryKey, cached]);
          const updated = cached.map((item) =>
            item.id === id ? { ...item, ...data } : item
          );
          queryClient.setQueryData(queryKey, updated);
        });
      return { previousQueries };
    },
    onSuccess: (_data, { silent }) => {
      if (!silent) toast.success('Actividad actualizada');
    },
    onError: (_err, { silent }, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, cached]) => {
          queryClient.setQueryData(queryKey, cached);
        });
      }
      if (!silent) toast.error('No se pudo actualizar la actividad');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
    },
  });
}

export function useEliminarActividad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => actividadesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Actividad eliminada');
    },
    onError: () => {
      toast.error('No se pudo eliminar la actividad');
    },
  });
}

export function useCompletarActividad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, completarSubtareas }: { id: string; completarSubtareas?: boolean }) =>
      actividadesService.complete(id, completarSubtareas),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Actividad completada', {
        duration: 5000,
        action: {
          label: 'Deshacer',
          onClick: () => {
            actividadesService.reabrir(variables.id).then(() => {
              queryClient.invalidateQueries({ queryKey: ['actividades'] });
              toast.success('Actividad reabierta');
            }).catch(() => {
              toast.error('No se pudo deshacer');
            });
          },
        },
      });
    },
    onError: () => {
      toast.error('No se pudo completar la actividad');
    },
  });
}

export function useReabrirActividad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => actividadesService.reabrir(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Actividad reabierta');
    },
    onError: () => {
      toast.error('No se pudo reabrir la actividad');
    },
  });
}

// Smart Quick Create (with semantic parser + resolver)
export function useSmartQuickCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      titulo: string;
      area?: string;
      prioridad?: string;
      clienteRef?: string;
      contactoRef?: string;
      tipoCodigo?: string;
      proyectoRef?: string;
      ubicacionRef?: string;
      etiquetaRefs?: string[];
      fechaAgendada?: string;
      horaAgendada?: string;
      fechaLimite?: string;
      // Pre-resolved IDs from Fuse.js (skip backend re-resolution)
      resolvedClienteId?: string;
      resolvedContactoId?: string;
      // Subtask titles parsed from // separator
      subtareas?: string[];
    }) => {
      let resolved: Partial<ActividadCreate> = {};
      const warnings: string[] = [];

      // Use pre-resolved IDs from Fuse.js when available
      if (data.resolvedClienteId) resolved.cliente_id = data.resolvedClienteId;
      if (data.resolvedContactoId) resolved.contacto_id = data.resolvedContactoId;

      // Only call backend resolver for refs NOT already resolved
      const needsBackendResolve =
        (!data.resolvedClienteId && data.clienteRef) ||
        (!data.resolvedContactoId && data.contactoRef) ||
        data.tipoCodigo || data.proyectoRef || data.ubicacionRef ||
        (data.etiquetaRefs && data.etiquetaRefs.length > 0);

      if (needsBackendResolve) {
        try {
          const refResult = await actividadesService.resolverRefs({
            cliente_ref: !data.resolvedClienteId ? data.clienteRef : undefined,
            contacto_ref: !data.resolvedContactoId ? data.contactoRef : undefined,
            tipo_codigo: data.tipoCodigo,
            proyecto_ref: data.proyectoRef,
            ubicacion_ref: data.ubicacionRef,
            etiqueta_refs: data.etiquetaRefs,
          });
          if (refResult.cliente_id && !resolved.cliente_id) resolved.cliente_id = refResult.cliente_id;
          if (refResult.contacto_id && !resolved.contacto_id) resolved.contacto_id = refResult.contacto_id;
          if (refResult.tipo_actividad_id) resolved.tipo_actividad_id = refResult.tipo_actividad_id;
          warnings.push(...refResult.warnings);
        } catch {
          // Fire and forget — create with just the title
          warnings.push('No se pudieron resolver las referencias');
        }
      }

      const createData: ActividadCreate = {
        titulo: data.titulo,
        area: (data.area as ActividadCreate['area']) ?? 'PERSONAL',
        prioridad: (data.prioridad as ActividadCreate['prioridad']) ?? 'A',
        fecha_agendada: data.fechaAgendada,
        hora_agendada: data.horaAgendada,
        fecha_limite: data.fechaLimite,
        ...resolved,
      };

      const actividad = await actividadesService.create(createData);

      // Create subtasks if any were parsed from // separator
      if (data.subtareas && data.subtareas.length > 0) {
        for (const subtitulo of data.subtareas) {
          try {
            await actividadesService.createSubtarea(actividad.id, {
              titulo: subtitulo,
              area: createData.area,
              prioridad: createData.prioridad,
            } as ActividadCreate);
          } catch {
            warnings.push(`No se pudo crear subtarea: ${subtitulo}`);
          }
        }
      }

      return { actividad, warnings };
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ['actividades'] });

      const previousQueries = queryClient.getQueriesData<ActividadListItem[]>({
        queryKey: ['actividades'],
      });

      const optimisticItem: ActividadListItem = {
        id: `temp-${Date.now()}`,
        titulo: data.titulo,
        area: (data.area as ActividadListItem['area']) ?? 'PERSONAL',
        estado: 'PENDIENTE',
        prioridad: (data.prioridad as ActividadListItem['prioridad']) ?? 'A',
        tiempo_registrado_min: 0,
        es_recurrente: false,
        es_plantilla: false,
        es_backlog: false,
        en_foco: false,
        origen: 'MANUAL',
      };

      queryClient.setQueriesData<ActividadListItem[]>(
        { queryKey: ['actividades'] },
        (old) => {
          if (!Array.isArray(old)) return old;
          return [optimisticItem, ...old];
        }
      );

      return { previousQueries };
    },
    onError: (err: Error, _data, context) => {
      if (context?.previousQueries) {
        for (const [queryKey, data] of context.previousQueries) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      toast.error(err.message || 'No se pudo crear la actividad');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
    },
  });
}

// Recurrencias
export function usePlantillasRecurrentes() {
  return useQuery({
    queryKey: ['actividades', 'recurrentes'],
    queryFn: () => actividadesService.getPlantillas(),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePausarPlantilla() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => actividadesService.pausarPlantilla(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Recurrencia pausada');
    },
    onError: () => {
      toast.error('No se pudo pausar la recurrencia');
    },
  });
}

export function useReactivarPlantilla() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => actividadesService.reactivarPlantilla(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Recurrencia reactivada');
    },
    onError: () => {
      toast.error('No se pudo reactivar la recurrencia');
    },
  });
}

// Historial
export function useActividadHistorial(actividadId: string) {
  return useQuery({
    queryKey: ['actividades', actividadId, 'historial'],
    queryFn: () => actividadesService.getHistorial(actividadId),
    enabled: !!actividadId,
  });
}

// Subtareas
export function useSubtareas(actividadId: string) {
  return useQuery({
    queryKey: ['actividades', actividadId, 'subtareas'],
    queryFn: () => actividadesService.getSubtareas(actividadId),
    enabled: !!actividadId,
  });
}

export function useCrearSubtarea() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ actividadId, data }: { actividadId: string; data: ActividadCreate }) =>
      actividadesService.createSubtarea(actividadId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success('Subtarea creada');
    },
    onError: () => {
      toast.error('No se pudo crear la subtarea');
    },
  });
}

export function useCrearSubtareasBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ actividadId, titulos }: { actividadId: string; titulos: string[] }) =>
      actividadesService.createSubtareasBatch(actividadId, titulos),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      toast.success(`${data.creadas} subtarea${data.creadas !== 1 ? 's' : ''} creada${data.creadas !== 1 ? 's' : ''}`);
    },
    onError: () => {
      toast.error('No se pudieron crear las subtareas');
    },
  });
}

// =============================================================================
// DRAG & DROP (REORDER + REPARENT)
// =============================================================================

export function useReordenarActividades() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: ReordenarItem[]) => actividadesService.reordenar(items),
    onMutate: async (newOrder: ReordenarItem[]) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['actividades'] });

      // Build a map of id → new orden
      const ordenMap = new Map(newOrder.map((item) => [item.id, item.orden]));

      // Snapshot all current actividades queries and apply optimistic reorder
      const previousQueries: [readonly unknown[], ActividadListItem[] | undefined][] = [];

      queryClient.getQueriesData<ActividadListItem[]>({ queryKey: ['actividades'] })
        .forEach(([queryKey, data]) => {
          if (!Array.isArray(data)) return;
          previousQueries.push([queryKey, data]);

          // Apply new orden values and re-sort
          const updated = data.map((item) => {
            const newOrden = ordenMap.get(item.id);
            return newOrden !== undefined ? { ...item, orden: newOrden } : item;
          });
          updated.sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999));

          queryClient.setQueryData(queryKey, updated);
        });

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      // Rollback to previous state
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error('No se pudo reordenar');
    },
    onSettled: () => {
      // Always refetch after mutation settles to ensure server state
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
    },
  });
}

export function useReparentActividad() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReparentRequest }) =>
      actividadesService.reparent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudo mover la actividad');
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
    },
  });
}

// =============================================================================
// ACCIONES MASIVAS (BULK)
// =============================================================================

function bulkToast(result: { actualizados: number; omitidos: number; errores: number }, accion: string) {
  if (result.omitidos > 0 || result.errores > 0) {
    toast.warning(
      `${result.actualizados} ${accion}, ${result.omitidos} omitidas${result.errores > 0 ? `, ${result.errores} con error` : ''}`
    );
  } else if (result.actualizados === 0) {
    toast.info(`No se ${accion === 'eliminadas' ? 'eliminó' : 'actualizó'} ninguna actividad`);
  } else {
    toast.success(`${result.actualizados} actividades ${accion}`);
  }
}

export function useBulkActualizarActividades() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkActualizarRequest) => actividadesService.bulkUpdate(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      bulkToast(result, 'actualizadas');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudieron actualizar las actividades');
    },
  });
}

export function useBulkCompletarActividades() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkCompletarRequest) => actividadesService.bulkComplete(data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      const count = result.actualizados;
      if (count > 0) {
        toast.success(`${count} actividades completadas`, {
          duration: 5000,
          action: {
            label: 'Deshacer',
            onClick: () => {
              Promise.all(variables.ids.map((id) => actividadesService.reabrir(id)))
                .then(() => {
                  queryClient.invalidateQueries({ queryKey: ['actividades'] });
                  toast.success(`${count} actividades reabiertas`);
                })
                .catch(() => {
                  toast.error('No se pudieron deshacer todas las acciones');
                });
            },
          },
        });
      } else {
        bulkToast(result, 'completadas');
      }
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudieron completar las actividades');
    },
  });
}

export function useBulkEliminarActividades() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkEliminarRequest) => actividadesService.bulkDelete(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      bulkToast(result, 'eliminadas');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'No se pudieron eliminar las actividades');
    },
  });
}

// =============================================================================
// BLOQUES (SECCIONES DEL DÍA)
// =============================================================================

export function useBloques(fecha: string) {
  return useQuery({
    queryKey: ['actividades', 'bloques', fecha],
    queryFn: () => actividadesService.listBloques(fecha),
    enabled: !!fecha,
  });
}

export function useCreateBloque() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { nombre: string; fecha: string; orden?: number; color?: string }) =>
      actividadesService.createBloque(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades', 'bloques'] });
    },
    onError: () => {
      toast.error('No se pudo crear la sección');
    },
  });
}

export function useUpdateBloque() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { nombre?: string; orden?: number; color?: string } }) =>
      actividadesService.updateBloque(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades', 'bloques'] });
    },
    onError: () => {
      toast.error('No se pudo actualizar la sección');
    },
  });
}

export function useDeleteBloque() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => actividadesService.deleteBloque(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades', 'bloques'] });
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
    },
    onError: () => {
      toast.error('No se pudo eliminar la sección');
    },
  });
}

// =============================================================================
// LIMPIEZA DIARIA
// =============================================================================

export function useLimpiezaDiaria() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => actividadesService.limpiezaDiaria(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
    },
  });
}
